import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { LocationNodeData } from '../../../@types/live-search.type';
import { useSearchAllocationNodes } from '../../../queries/live-search-query';

export interface AllocationFieldProps {
  label: string;
  value: string;
  onChange?: (nodeId: number, name: string) => void;
}

/** Allocation search popover for Technician. */
export const AllocationField: React.FC<AllocationFieldProps> = ({ label, value, onChange }) => {
  const anchorRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const searchMutation = useSearchAllocationNodes();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);

  useEffect(() => {
    if (!open || inputValue.length < 2) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      searchMutation.mutateAsync(inputValue).then(setOptions).catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, open]);

  const handleSelect = useCallback((_: unknown, node: LocationNodeData | null) => {
    if (node && onChange) {
      onChange(node.id, node.name);
    }
    setOpen(false);
  }, [onChange]);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '9px', fontWeight: 600, color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        ref={anchorRef}
        sx={{
          fontSize: '13px', fontWeight: 600, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          cursor: onChange ? 'pointer' : 'default',
          '&:hover': onChange ? { bgcolor: 'v4.tableHover', borderRadius: 1 } : {},
        }}
        onClick={() => { if (onChange) { setInputValue(''); setOptions([]); setOpen(true); } }}
      >
        {value || '—'}
      </Typography>
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1500 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper elevation={8} sx={{ width: 320, p: 1.5, mt: 0.5, border: '1px solid', borderColor: 'v4.border', borderRadius: 2 }}>
            <Autocomplete
              size="small"
              options={options}
              getOptionLabel={(opt) => opt.name}
              filterOptions={(x) => x}
              inputValue={inputValue}
              onInputChange={(_, val) => setInputValue(val)}
              onChange={handleSelect}
              loading={searchMutation.isPending}
              slotProps={{ popper: { sx: { zIndex: 1600 } } }}
              renderInput={(params) => (
                <MuiTextField {...params} placeholder="Search by name..."
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={`${option.id}-${option.code}`}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{option.name}</Typography>
                      <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>{option.nodeTypeName}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>{option.code}</Typography>
                  </Box>
                </li>
              )}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
